import React from 'react';
import {
    Alert,
    AlertTitle,
    Button,
    Box,
    IconButton,
    CircularProgress,
    Typography,
    Link,
} from '@mui/material';
import {
    VerifiedUser as VerifiedUserIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    HourglassEmpty as HourglassIcon,
    Close as CloseIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { KYCStatusValue, canRetryKYC } from '../../api/kyc';

interface KYCBannerProps {
    status: KYCStatusValue;
    rejectionReason?: string | null;
    onStartVerification: () => void;
    onDismiss?: () => void;
    isLoading?: boolean;
}

interface BannerConfig {
    severity: 'info' | 'warning' | 'error' | 'success';
    icon: React.ReactNode;
    title: string;
    message: string;
    buttonText?: string;
    buttonIcon?: React.ReactNode;
    showContactSupport?: boolean;
}

const getBannerConfig = (
    status: KYCStatusValue,
    rejectionReason?: string | null
): BannerConfig | null => {
    switch (status) {
        case 'PENDING':
            return {
                severity: 'info',
                icon: <VerifiedUserIcon />,
                title: 'Verify Your Identity',
                message: 'Complete identity verification to start trading on Radix.',
                buttonText: 'Verify Now',
            };

        case 'IN_REVIEW':
            return {
                severity: 'info',
                icon: <HourglassIcon />,
                title: 'Verification In Progress',
                message: 'Your verification is being reviewed. This usually takes a few minutes.',
            };

        case 'ACTION_REQUIRED':
            return {
                severity: 'warning',
                icon: <WarningIcon />,
                title: 'Additional Information Required',
                message: 'Please provide additional information to complete your verification.',
                buttonText: 'Continue Verification',
            };

        case 'ON_HOLD':
            return {
                severity: 'warning',
                icon: <HourglassIcon />,
                title: 'Verification Under Review',
                message: 'Your verification requires additional review. We\'ll notify you when it\'s complete.',
            };

        case 'REJECTED':
            return {
                severity: 'error',
                icon: <ErrorIcon />,
                title: 'Verification Failed',
                message: rejectionReason || 'Your verification was unsuccessful. Please try again.',
                buttonText: 'Try Again',
                buttonIcon: <RefreshIcon />,
            };

        case 'REJECTED_FINAL':
            return {
                severity: 'error',
                icon: <ErrorIcon />,
                title: 'Verification Failed',
                message: 'Your verification was unsuccessful and cannot be retried.',
                showContactSupport: true,
            };

        case 'SUSPENDED':
            return {
                severity: 'error',
                icon: <ErrorIcon />,
                title: 'Account Suspended',
                message: 'Your account has been suspended.',
                showContactSupport: true,
            };

        case 'EXPIRED':
            return {
                severity: 'warning',
                icon: <WarningIcon />,
                title: 'Verification Expired',
                message: 'Your identity verification has expired. Please verify again to continue trading.',
                buttonText: 'Reverify',
                buttonIcon: <RefreshIcon />,
            };

        case 'APPROVED':
            // No banner needed for approved status
            return null;

        default:
            return null;
    }
};

const KYCBanner: React.FC<KYCBannerProps> = ({
    status,
    rejectionReason,
    onStartVerification,
    onDismiss,
    isLoading = false,
}) => {
    const config = getBannerConfig(status, rejectionReason);

    if (!config) {
        return null;
    }

    const canRetry = canRetryKYC(status);

    return (
        <Alert
            severity={config.severity}
            icon={config.icon}
            sx={{
                mb: 3,
                alignItems: 'flex-start',
                '& .MuiAlert-message': {
                    width: '100%',
                },
            }}
            action={
                onDismiss && (
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={onDismiss}
                    >
                        <CloseIcon fontSize="inherit" />
                    </IconButton>
                )
            }
        >
            <AlertTitle sx={{ fontWeight: 600 }}>{config.title}</AlertTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="body2">{config.message}</Typography>

                {config.showContactSupport && (
                    <Typography variant="body2">
                        Please{' '}
                        <Link
                            href="mailto:support@radix.trading"
                            underline="hover"
                            sx={{ fontWeight: 500 }}
                        >
                            contact support
                        </Link>{' '}
                        for assistance.
                    </Typography>
                )}

                {config.buttonText && canRetry && (
                    <Box sx={{ mt: 1 }}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onStartVerification}
                            disabled={isLoading}
                            startIcon={
                                isLoading ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    config.buttonIcon
                                )
                            }
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                            }}
                        >
                            {config.buttonText}
                        </Button>
                    </Box>
                )}
            </Box>
        </Alert>
    );
};

export default KYCBanner;
